import { useState } from "react";
import {
  activeItems,
  BILLING_INTERVALS,
  billingItemLabel,
  formatCentsUsd,
  formatInterval,
  INTEGRATION_SLOT_ITEM_CODE,
  isPlanItem,
  lineTotalCents,
  stripeDashboardSubscriptionUrl,
  type SlotEntitlement,
  type Subscription,
} from "../../../types/billing";
import { formatDateTimeCompact } from "../../../utils/formatDateTime";
import styles from "./SubscriptionDetail.module.css";

export type TierOption = { readonly tierCode: string; readonly displayName: string };

// Shared, presentational body for a single subscription: the slot-entitlement
// summary, the line-item table (with per-slot quantity / remove controls), and
// the management actions. Used inline by the instance-detail Billing panel and
// inside the fleet-page detail modal. All fetching + mutation wiring lives in
// the container; this component only renders and calls back.

export type SubscriptionDetailNotice = { readonly tone: "ok" | "error"; readonly text: string } | null;

type SubscriptionDetailProps = {
  readonly subscription: Subscription;
  readonly entitlement: SlotEntitlement | null;
  readonly tiers: readonly TierOption[];
  readonly busy: boolean;
  readonly notice: SubscriptionDetailNotice;
  readonly onChangeQuantity: (stripeItemId: string, quantity: number) => void;
  readonly onRemoveItem: (stripeItemId: string) => void;
  readonly onSetTier: (tier: string, interval: string) => void;
  readonly onSync: () => void;
  readonly onOpenPortal: () => void;
  // Optional: when provided, a Close button is rendered inline with the other
  // actions (used by the modal so Close sits on the same row, not a separate
  // footer line). Omitted by the inline instance-detail panel.
  readonly onClose?: () => void;
};

// Tier + interval picker. Tier comes from the entitlement (the tenant's tier);
// interval from the base-plan line. Applying reprices the base plan to the new
// (tier, interval) price. Only shown for tenant-linked subscriptions.
const PlanControl = ({
  tiers,
  currentTier,
  currentInterval,
  busy,
  onApply,
}: {
  readonly tiers: readonly TierOption[];
  readonly currentTier: string | null;
  readonly currentInterval: string;
  readonly busy: boolean;
  readonly onApply: (tier: string, interval: string) => void;
}): JSX.Element => {
  const [tier, setTier] = useState(currentTier ?? tiers[0]?.tierCode ?? "");
  const [interval, setInterval] = useState(currentInterval);
  const unchanged = tier === (currentTier ?? "") && interval === currentInterval;

  // The tenant's current tier may be inactive (filtered out of `tiers`), and the
  // base plan may bill on an interval we don't otherwise offer (day/week). Keep
  // such current values selectable so the control reflects reality rather than
  // silently snapping to the first option.
  const tierMissing = currentTier != null && !tiers.some((t) => t.tierCode === currentTier);
  const intervalMissing = !(BILLING_INTERVALS as readonly string[]).includes(interval);

  return (
    <div className={styles.plan}>
      <span className={styles.planLabel}>Plan</span>
      <select
        className={styles.planSelect}
        value={tier}
        disabled={busy}
        onChange={(e) => setTier(e.target.value)}
        aria-label="Tier"
      >
        {currentTier == null && <option value="">Select tier…</option>}
        {tierMissing && <option value={currentTier ?? ""}>{currentTier} (inactive)</option>}
        {tiers.map((t) => (
          <option key={t.tierCode} value={t.tierCode}>
            {t.displayName}
          </option>
        ))}
      </select>
      <select
        className={styles.planSelect}
        value={interval}
        disabled={busy}
        onChange={(e) => setInterval(e.target.value)}
        aria-label="Billing interval"
      >
        {intervalMissing && <option value={interval}>{formatInterval(interval)}</option>}
        {BILLING_INTERVALS.map((iv) => (
          <option key={iv} value={iv}>
            {formatInterval(iv)}
          </option>
        ))}
      </select>
      <button
        type="button"
        className={styles.secondaryBtn}
        disabled={busy || unchanged || tier === ""}
        onClick={() => onApply(tier, interval)}
      >
        Apply
      </button>
    </div>
  );
};

const SlotsSummary = ({ entitlement }: { entitlement: SlotEntitlement }): JSX.Element => {
  const { includedSlots, purchasedSlots, inUse, allowance, remaining } = entitlement;
  const bonusSlots = entitlement.bonusSlots ?? 0;
  // Built as one string: adjacent JSX expressions would collapse to stray
  // double spaces when the optional bonus term is absent.
  const breakdown = [
    `${includedSlots} included${entitlement.tier != null ? ` (${entitlement.tier})` : ""}`,
    ...(bonusSlots > 0 ? [`${bonusSlots} bonus`] : []),
    `${purchasedSlots} purchased`,
  ].join(" + ");
  const pct = allowance > 0 ? Math.min(100, Math.round((inUse / allowance) * 100)) : 0;
  const fill = inUse >= allowance && allowance > 0 ? styles.fillFull : styles.fill;
  return (
    <div className={styles.slots}>
      <div className={styles.slotsHead}>
        <span className={styles.slotsLabel}>Integration slots</span>
        <span className={styles.slotsCount}>
          {inUse} used / {allowance} allowed
        </span>
      </div>
      <div className={styles.track}>
        <span className={fill} style={{ width: `${pct}%` }} />
      </div>
      <p className={styles.slotsCaption}>
        {breakdown}
        {" · "}
        {remaining} remaining
      </p>
    </div>
  );
};

export const SubscriptionDetail = ({
  subscription,
  entitlement,
  tiers,
  busy,
  notice,
  onChangeQuantity,
  onRemoveItem,
  onSetTier,
  onSync,
  onOpenPortal,
  onClose,
}: SubscriptionDetailProps): JSX.Element => {
  const items = activeItems(subscription);
  const basePlanInterval = items.find((i) => isPlanItem(i.itemCode))?.interval ?? "month";
  return (
    <div className={styles.detail}>
      {entitlement != null && <SlotsSummary entitlement={entitlement} />}

      {entitlement != null && tiers.length > 0 && (
        // Remount when the server-side tier/interval changes (e.g. after a Sync
        // or the apply itself) so the picker re-seeds from truth instead of
        // holding a stale selection -- same idiom as the pricing-grid cells.
        <PlanControl
          key={`${entitlement.tier ?? ""}:${basePlanInterval}`}
          tiers={tiers}
          currentTier={entitlement.tier}
          currentInterval={basePlanInterval}
          busy={busy}
          onApply={onSetTier}
        />
      )}

      <table className={styles.table}>
        <thead>
          <tr>
            <th>Item</th>
            <th>Interval</th>
            <th>Renews</th>
            <th className={styles.qtyCol}>Qty</th>
            <th className={styles.num}>Unit</th>
            <th className={styles.num}>Line</th>
            <th aria-label="Actions" />
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const isSlot = item.itemCode === INTEGRATION_SLOT_ITEM_CODE;
            return (
              <tr key={item.stripeItemId}>
                <td>{item.displayName ?? billingItemLabel(item.itemCode)}</td>
                <td>{item.interval ?? "—"}</td>
                <td>{item.currentPeriodEnd != null ? formatDateTimeCompact(item.currentPeriodEnd) : "—"}</td>
                <td className={styles.qtyCol}>
                  {isSlot ? (
                    <span className={styles.qty}>
                      <button
                        type="button"
                        className={styles.stepper}
                        disabled={busy || item.quantity <= 1}
                        onClick={() => onChangeQuantity(item.stripeItemId, item.quantity - 1)}
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>
                      <span className={styles.qtyValue}>{item.quantity}</span>
                      <button
                        type="button"
                        className={styles.stepper}
                        disabled={busy}
                        onClick={() => onChangeQuantity(item.stripeItemId, item.quantity + 1)}
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </span>
                  ) : (
                    item.quantity
                  )}
                </td>
                <td className={styles.num}>{formatCentsUsd(item.unitAmountCents)}</td>
                <td className={styles.num}>{formatCentsUsd(lineTotalCents(item))}</td>
                <td className={styles.rowActions}>
                  {isSlot && (
                    <button
                      type="button"
                      className={styles.removeBtn}
                      disabled={busy}
                      onClick={() => onRemoveItem(item.stripeItemId)}
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {notice != null && (
        <p className={notice.tone === "error" ? styles.noticeError : styles.noticeOk}>{notice.text}</p>
      )}

      <div className={styles.actions}>
        <button type="button" className={styles.secondaryBtn} disabled={busy} onClick={onOpenPortal}>
          Open billing portal
        </button>
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={() =>
            window.open(
              stripeDashboardSubscriptionUrl(subscription.stripeSubscriptionId),
              "_blank",
              "noopener,noreferrer",
            )
          }
        >
          Open in Stripe
        </button>
        <button type="button" className={styles.secondaryBtn} disabled={busy} onClick={onSync}>
          Sync
        </button>
        {onClose !== undefined ? (
          <button type="button" className={styles.secondaryBtn} onClick={onClose}>
            Close
          </button>
        ) : null}
      </div>
    </div>
  );
};
